/**
 * 请求封装工具类
 */
import axios, { AxiosRequestConfig, AxiosResponse, Canceler } from 'axios';
import { identity, pickBy } from 'lodash';
import log from 'loglevel';
import qs from 'qs';
import webSecurity from 'web-security';
import BizException from '../exceptions/BizException';
import { ErrorCode } from '../exceptions/ErrorCode';
import { uuid } from './UUID';
// import { redirect2Login } from "./CommonUtil";
import { correctionServerTime, getClientHeader, getServerTimestamp } from './DateUtil';
import { parseBigJSON } from './JSONParse.js';
import { encryptMD5 } from './SecurityUtil';
import { getAccessToken, isMobileClient } from './CommonUtil';
import { notQTClient } from '../api/FileApi';
import { getQuery } from './UrlUtil';
// import { getAuthorizationHeader, setToken, tokenKey } from "./TokenUtil";

const CancelToken = axios.CancelToken;

export enum Method {
  GET = 'get',
  POST = 'post',
  PUT = 'put',
  DELETE = 'delete',
  HEAD = 'head',
}

interface RequestHeader {
  [key: string]: string | null;
}

type ResponseType = 'arraybuffer' | 'blob' | 'document' | 'json' | 'text' | 'stream' | undefined;
/**
 * 请求参数
 */
export interface RequestParam {
  url: string;
  method: Method;
  teamId?: string;
  data?: any;
  timeout?: number;
  formData?: any;
  retry?: number;
  headers?: RequestHeader;
  cancelExecutor?: (canceler: Canceler) => void;
  isNotNeedHeader?: boolean;
  responseType?: string; // 表示服务器响应的数据类型，可以是 'arraybuffer', 'blob', 'document', 'json', 'text', 'stream'
}

interface CustomRequestConfig extends AxiosRequestConfig {
  url: string;
  teamId?: string;
  requestId: string; // 每次请求的唯一标识ID
  requestFilterKey: string; // 用于过滤请求的key
  retry: number; // 请求重试次数
  oriParam: any; // 请求的原始数据
}

interface FilterUrl {
  url: string;
  keyGenerator: (config: CustomRequestConfig) => string;
}

// 需要过滤请求的url配置
// 通过keyGenerator生成请求标识，标识一致，则认为请求一致
const filterUrlConfig: { [url: string]: FilterUrl } = {};

function getRequestKey(config: CustomRequestConfig): string {
  const item = filterUrlConfig[config.url];
  if (item) {
    return item.keyGenerator(config);
  }
  // 没有找到配置的，根据url + 排序参数的MD5生成一个key
  const paramMD5 = encryptMD5(webSecurity.sortParam(config.oriParam));
  return `${config.url}_${paramMD5}`;
}

// 正在pending的请求
const pendingRequest: any = {};
const pendingRequestTimer: any = {};

function pushPendingRequest(config: CustomRequestConfig, data: any) {
  const key = config.requestFilterKey;
  if (pendingRequest[key]) {
    pendingRequest[key].push(data);
  } else {
    pendingRequest[key] = [];
    pendingRequest[key].push(data);
    if (config.timeout) {
      addRequestTimeoutTimer(key, config.timeout);
    }
  }
}

function addRequestTimeoutTimer(requestFilterKey: string, timeout: number) {
  clearRequestTimeoutTimer(requestFilterKey);
  pendingRequestTimer[requestFilterKey] = window.setTimeout(() => {
    execRequestReject(
      requestFilterKey,
      new BizException(`[${ErrorCode.TIME_OUT}]execRequest request timeout ${requestFilterKey}`)
    );
  }, timeout + 1000);
}

function clearRequestTimeoutTimer(requestFilterKey: string) {
  window.clearTimeout(pendingRequestTimer[requestFilterKey]);
  delete pendingRequestTimer[requestFilterKey];
}

function isRequestPending(config: CustomRequestConfig): boolean {
  const requestList = pendingRequest[config.requestFilterKey] || [];
  return requestList.length > 1;
}

function execRequestResolve(requestFilterKey: string, data: any): void {
  const requestList = pendingRequest[requestFilterKey];
  for (const request of requestList || []) {
    request.resolve(data);
  }
  clearRequestTimeoutTimer(requestFilterKey);
  delete pendingRequest[requestFilterKey];
}

function execRequestReject(requestFilterKey: string, error: Error): void {
  const requestList = pendingRequest[requestFilterKey];
  for (const request of requestList || []) {
    request.reject(error);
  }
  clearRequestTimeoutTimer(requestFilterKey);
  delete pendingRequest[requestFilterKey];
}

// 参数序列化
axios.defaults.paramsSerializer = (params) => {
  return qs.stringify(params, { indices: false });
};

function handleResponse(response: AxiosResponse) {
  const originalRequest = response.config as CustomRequestConfig;
  const data = response.data || {};
  correctionServerTime(response.headers.date); // 校正服务器时间x
  // 其他响应接口
  if (data.code && data.code !== 0 && data.code !== 200) {
    log.warn(
      '[request failed]',
      'url',
      originalRequest.url,
      'teamId',
      originalRequest.teamId,
      data.code,
      data.message
    );

    if (`${data.code}` === ErrorCode.TOKEN_INVALIDATE) {
      // 弹出登录
      //   redirect2Login();
    }

    // 如果响应头有token，更新token
    // if (response.headers[tokenKey]) {
    //   setToken(response.headers[tokenKey]);
    // }

    return Promise.reject(new BizException(`[${data.code}]${data.message || ''}`, data.data));
  }

  // 正常响应接口
  return Promise.resolve(data);
}
// 添加响应拦截器
axios.interceptors.response.use(
  (response) => {
    if (
      response.config.url?.indexOf('login/sms/send') !== -1 ||
      response.config.url.indexOf('verification/code') !== -1 ||
      response.config.url.indexOf('register/default') !== -1 ||
      response.config.url.indexOf('register/code/send') !== -1 ||
      response.config.url.indexOf('register/code/check') !== -1
    ) {
      const data = response.data || {};
      correctionServerTime(response.headers.date); // 校正服务器时间
      if (data.ok === false) {
        // 旧版接口：ok===false
        return Promise.reject(new BizException(data.error));
      }
      if (data.code && data.code !== 0 && data.code !== 200) {
        // 新版接口：有code，且code值不为0和200，请求返回失败
        return Promise.reject(new BizException(`[${data.code}]${data.message || ''}`));
      }
      // 正常响应接口
      return Promise.resolve(data);
    } else {
      // 探测url的请求不做处理
      if (response.config.method !== Method.HEAD) {
        return handleResponse(response);
      }
      return response;
    }
  },
  (error) => {
    const res = error.response;
    // 来自轻推400以上的状态码，按正常响应处理
    if (
      res &&
      res.data &&
      res.headers['content-type'] &&
      res.headers['content-type'].indexOf('application/json') !== -1 &&
      res.data.code
    ) {
      return handleResponse(res);
    }

    let errorMsg = ErrorCode.REQUEST_FAILED;
    if (res) {
      const status = res.status;
      if (status === 404) {
        errorMsg = ErrorCode.NOT_FOUND;
      }
    } else if (error.code === 'ECONNABORTED') {
      // 超时
      errorMsg = ErrorCode.TIME_OUT;
    }
    // 请求出错，如网络连接不通
    // 超时，404等，直接抛回异常
    return Promise.reject(new BizException(errorMsg));
  }
);

/**
 *
 * @param {RequestParam} param
 * @param param.teamId
 * @param param.timeout default: 20000
 */
export function sendRequest(param: RequestParam) {
  return new Promise<any>((resolve, reject) => {
    // 组装请求参数
    // log.info('请求 ' + param.url);
    const headers: any = {
      // Authorization: getAuthorizationHeader(),
      'Content-Language': 'zh',
      'Content-Type':
        param.method === Method.DELETE || param.method === Method.PUT
          ? 'application/x-www-form-urlencoded'
          : undefined,

      ...param.headers,
    };
    if (!isMobileClient()) {
      // 不是手机端才加
      headers.Authorization = 'Bearer ' + getAccessToken();
    }
    if (isMobileClient() && notQTClient && (getAccessToken() || getQuery('accessToken'))) {
      // 如果是短信登录过的非轻推客户端加
      const queryToken = getQuery('accessToken');
      const localToken = getAccessToken();
      headers.Authorization = 'Bearer ' + (queryToken ? queryToken : localToken);
    }
    // 不需要header的时候，设置为空
    if (
      param.url.indexOf('login/sms/send') >= 0 ||
      param.url.indexOf('verification/code') >= 0 ||
      param.url.indexOf('register/default') >= 0 ||
      param.url.indexOf('register/code/send') >= 0 ||
      param.url.indexOf('register/code/check') >= 0
    ) {
      // 这几个接口需要signature
      headers.CLIENT = getClientHeader();
      headers.Signature = webSecurity.signUrl(
        '',
        param.data,
        getServerTimestamp(),
        getClientHeader()
      );
    }
    const config: CustomRequestConfig = {
      baseURL: '/api',
      url: param.url,
      method: param.method,
      headers: param.isNotNeedHeader ? {} : pickBy(headers, identity),
      cancelToken: new CancelToken((canceler) => {
        if (typeof param.cancelExecutor === 'function') {
          param.cancelExecutor(canceler);
        }
      }),
      oriParam: param.data,
      requestId: uuid(),
      requestFilterKey: '',
      retry: typeof param.retry === 'number' ? param.retry : 2,
      timeout: typeof param.timeout === 'number' ? param.timeout : 10000,
      transformResponse: (data: any) => {
        if (typeof data === 'string') {
          try {
            return parseBigJSON(data);
          } catch (e) {
            log.error('transformResponse', e);
            return data;
          }
        }
        return data;
      },
    };

    if (param.responseType) {
      config.responseType = param.responseType as ResponseType;
    }
    if (config.method === Method.GET) {
      config.params = param.data;
    } else {
      config.data = qs.stringify(param.data, { indices: false });
    }

    if (param.formData) {
      config.data = param.formData;
    }

    config.requestFilterKey = getRequestKey(config);
    // 存入pending列表中
    pushPendingRequest(config, {
      resolve,
      reject,
      requestId: config.requestId,
    });
    if (!isRequestPending(config)) {
      // 没有正在发送的请求
      // 发送该请求，请求成功回调会在response的拦截器中执行
      axios(config)
        .then((response) => {
          execRequestResolve(config.requestFilterKey, response);
        })
        .catch((error) => {
          execRequestReject(config.requestFilterKey, error);
        });
    } else {
      // log.warn('RequestUtil', '有请求正在进行中', config);
    }
  });
}
